import { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { ssoGroupsMappingText } from "../../../../assets/texts/Settings/sso";
import { BottomAppBar } from "../../../../Components/BottomAppBar";
import { DraggableContainer } from "../../../../Components/DragAndDrop/DraggableContainer";
import { DraggableGroup } from "../../../../Components/DragAndDrop/DraggableGroup";
import { useRoles } from "../../../../Components/hooks/IAM/useRoles";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { getDefaultRoleRefByCustomer } from "../../../../utils/permissions";
import { useSsoGroupMapping } from "./hooks/useSsoGroupMapping";
import SsoAddMappingButton from "./SsoAddMappingButton";
import SsoGroupMappingHeaders from "./SsoGroupMappingHeaders";
import SsoGroupMappingItem from "./SsoGroupMappingItem";

export type GroupApiMapping = {
  groupId: string;
  roleAtDoit: string;
  org: string;
};

export type GroupMapping = {
  groupId: string;
  role: string;
  organization: string;
};

export type SsoGroupMappingListProps = {
  attributeKey: string;
  onAttributeKeyChange: (value: string) => void;
  ssoEnabled?: boolean;
};

export type IdNameMapping = {
  id: string;
  name: string;
};

export const cyIds = {
  list: "ssoGroupMappingList",
  item: (id: string): string => `ssoGroupMappingItem-${id}`,
  bottomAppBar: "sabmit-sso-group-mappings",
  buttons: {
    add: "ssoGroupMappingAddButton",
    cancel: "ssoGroupMappingCancelButton",
    save: "ssoGroupMappingSaveButton",
  },
  headers: "ssoGroupMappingHeaders",
};

const SsoGroupMappingList = ({ attributeKey, onAttributeKeyChange, ssoEnabled = true }: SsoGroupMappingListProps) => {
  const { roles } = useRoles();
  const { organizations, customer } = useCustomerContext();

  const defaultRoleId = getDefaultRoleRefByCustomer(customer)?.id;

  const {
    isContentUpdated,
    userGroups,
    items,
    loading,
    error,
    saving,
    defaultMapping,
    handleItemsOrder,
    handleMappingChange,
    handleDelete,
    handleAddMapping,
    handleSaveChanges,
    handleReset,
  } = useSsoGroupMapping(customer.id, attributeKey, onAttributeKeyChange);

  const rolesList = useMemo<IdNameMapping[]>(
    () =>
      roles
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        .map((role) => ({
          id: role.id,
          name: role.id === defaultRoleId ? `${role.name} (Default)` : role.name,
        })),
    [defaultRoleId, roles]
  );

  const orgsList = useMemo<IdNameMapping[]>(
    () =>
      organizations
        .sort((a, b) => a.data.name.localeCompare(b.data.name, undefined, { sensitivity: "base" }))
        .map((org) => ({
          id: org.ref.id,
          name: org.data.name,
        })),
    [organizations]
  );

  const showOrganizationColumn = useMemo<boolean>(() => orgsList.length > 1, [orgsList.length]);

  // Track validation errors for each item
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});

  const handleValidationChange = useCallback((id: string, hasError: boolean) => {
    setValidationErrors((prev) => ({
      ...prev,
      [id]: hasError,
    }));
  }, []);

  const hasValidationErrors = useMemo(
    () => Object.values(validationErrors).some((hasError) => hasError),
    [validationErrors]
  );

  const existingGroupIdsWithoutCurrent = useCallback<(rowId: string) => string[]>(
    (rowId: string) =>
      Object.entries(userGroups)
        .filter(([id, group]) => id !== rowId && group.groupId !== "" && group.groupId !== defaultMapping.groupId)
        .map(([_, group]) => group.groupId),
    [userGroups, defaultMapping.groupId]
  );

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Typography color="error">Failed to load SSO group mappings: {error}</Typography>;
  }

  return (
    <Stack spacing={2} data-cy={cyIds.list}>
      <DraggableContainer
        values={items}
        onItemsOrderChanged={handleItemsOrder}
        mouseSensorOptions={{ activationConstraint: { delay: 100, tolerance: 5 } }}
        renderDraggingItem={(id) => (
          <SsoGroupMappingItem
            id={id}
            rolesList={rolesList}
            orgsList={orgsList}
            grabbing
            {...userGroups[id]}
            onDelete={() => {
              handleDelete(id);
            }}
            onChange={handleMappingChange}
            showOrganizationColumn={showOrganizationColumn}
            disabled={!ssoEnabled}
            existingGroupIds={existingGroupIdsWithoutCurrent(id)}
            onValidationChange={handleValidationChange}
          />
        )}
      >
        <Stack direction="column" sx={{ width: "100%" }}>
          <SsoAddMappingButton onAdd={handleAddMapping} disabled={!ssoEnabled} />
          <SsoGroupMappingHeaders showOrganizationColumn={showOrganizationColumn} />
          <DraggableGroup
            id="main"
            renderContent={(items) => (
              <Box>
                {items.map((id) => (
                  <SsoGroupMappingItem
                    key={id}
                    id={id}
                    rolesList={rolesList}
                    orgsList={orgsList}
                    {...userGroups[id]}
                    onDelete={() => {
                      handleDelete(id);
                    }}
                    onChange={handleMappingChange}
                    showOrganizationColumn={showOrganizationColumn}
                    disabled={!ssoEnabled}
                    existingGroupIds={existingGroupIdsWithoutCurrent(id)}
                    onValidationChange={handleValidationChange}
                  />
                ))}
              </Box>
            )}
          />
          <SsoGroupMappingItem
            key={ssoGroupsMappingText.ROLE_ID_DEFAULT}
            id={ssoGroupsMappingText.ROLE_ID_DEFAULT}
            rolesList={rolesList}
            orgsList={orgsList}
            {...defaultMapping}
            onDelete={() => {}}
            onChange={() => {}}
            showOrganizationColumn={showOrganizationColumn}
            disabled={true}
            onValidationChange={handleValidationChange}
          />
        </Stack>
      </DraggableContainer>
      <BottomAppBar
        maxWidth="md"
        primaryButtonName={saving ? ssoGroupsMappingText.BUTTON_SAVING : ssoGroupsMappingText.BUTTON_SAVE}
        primaryButtonProps={{ "data-cy": cyIds.buttons.save }}
        handlePrimaryButtonClicked={handleSaveChanges}
        secondaryButtonName={ssoGroupsMappingText.BUTTON_CANCEL}
        secondaryButtonProps={{ "data-cy": cyIds.buttons.cancel }}
        handleSecondaryButtonClicked={handleReset}
        disabled={saving || !isContentUpdated || !ssoEnabled || hasValidationErrors}
        data-cy={cyIds.bottomAppBar}
      />
    </Stack>
  );
};

export default SsoGroupMappingList;
